import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Typography } from '@chilecompra/react-kit/components'
import { formatDate } from '@chilecompra/react-kit/utils'
import useResponsive from '../../modules/hooks/responsive'
import { onMainIndicatorDetailsThunk } from './MainIndicators.actions'
import IconButton from '../../components/Buttons/IconButton'
import { Card, GridWrapper, InlineLabel, Link } from './MainIndicators.styles'

/**
 * The MainIndicators' container.
 */
const MainIndicators = forwardRef(({ idEntity, licMas, licMin, licDes, year, onOpen }, ref) => {
  const dispatch = useDispatch()
  const {
    details: { tableStructure },
    fetching: loading
  } = useSelector(state => state.mainIndicators)
  const [category, setCategory] = useState('')
  const [showTable, setShowTable] = useState(false)
  const dataTableRef = useRef()
  const r = useResponsive()
  const handleLicMas = () => {
    dispatch(onMainIndicatorDetailsThunk({ year, id: idEntity, type: 'lic_mas_ofertas' }))
    onOpen('indicators')
    setShowTable(true)
    setCategory('Licitaciones con 3 o más ofertas')
  }
  const handleLicMin = () => {
    dispatch(onMainIndicatorDetailsThunk({ year, id: idEntity, type: 'lic_menos_ofertas' }))
    onOpen('indicators')
    setShowTable(true)
    setCategory('Licitaciones con 2 o menos ofertas')
  }
  const handleLicDes = () => {
    dispatch(onMainIndicatorDetailsThunk({ year, id: idEntity, type: 'lic_desiertas' }))
    onOpen('indicators')
    setShowTable(true)
    setCategory('Licitaciones declaradas desiertas')
  }
  const handleReturn = () => {
    onOpen(null)
    setShowTable(false)
    setCategory('')
  }

  useEffect(() => {
    dataTableRef.current?.setPage(1)
  }, [year])

  useImperativeHandle(ref, () => ({
    close: () => {
      setShowTable(false)
      setCategory('')
    }
  }))

  return (
    <GridWrapper container justifyContent="center" spacing={1}>
      <GridWrapper item xs={12} padding="0 0 16px">
        <Typography
          fontSize="14px"
          fontWeight={category ? 'regular' : 'bold'}
          padding="0 0 16px"
          margin="24px 0 0"
          variant="body2"
        >
          Principales indicadores {category ? <strong> / {category}</strong> : null}
        </Typography>
        {showTable ? (
          <IconButton type="back" onClick={handleReturn} fontWeight="bold" fontSize="14px" padding="8px 0 0">
            <InlineLabel>Volver a los indicadores</InlineLabel>
          </IconButton>
        ) : null}
      </GridWrapper>
      {!showTable ? (
        <>
          <GridWrapper
            item
            xs={10}
            md={4}
            padding={r({ xs: '0 8px', md: '0 8px 0 0' })}
            margin={r({ xs: '16px 0 16px 0', md: '0 0 0 0' })}
          >
            {licMas.value === undefined ? (
              <Card enableHover color="blank" elevation="1" rows={1}>
                <Typography padding="8px 0" variant="body2">
                  Sin licitaciones con 3 o más ofertas
                </Typography>
              </Card>
            ) : (
              <Card enableHover color="blank" elevation="1" onClick={handleLicMas}>
                <Typography padding="0 0 8px" variant="body2">
                  <strong style={{ fontSize: '24px', marginRight: '10px' }}>{licMas.value}</strong> Licitaciones con 3 o
                  o más ofertas
                </Typography>
                <Typography color="primary" fontSize="12px" variant="body2">
                  Ver detalle
                </Typography>
              </Card>
            )}
          </GridWrapper>
          <GridWrapper item xs={10} md={4} padding="0 8px" margin={r({ xs: '16px 0 16px 0', md: '0 0 0 0' })}>
            {licMin.value === undefined ? (
              <Card enableHover color="blank" elevation="1" rows={1}>
                <Typography padding="8px 0" variant="body2">
                  Sin licitaciones con 2 o menos ofertas
                </Typography>
              </Card>
            ) : (
              <Card enableHover color="blank" elevation="1" onClick={handleLicMin}>
                <Typography padding="0 0 8px" variant="body2">
                  <strong style={{ fontSize: '24px', marginRight: '10px' }}>{licMin.value}</strong>
                  Licitaciones con 2 o menos ofertas
                </Typography>
                <Typography color="primary" fontSize="12px" variant="body2">
                  Ver detalle
                </Typography>
              </Card>
            )}
          </GridWrapper>
          <GridWrapper
            item
            xs={10}
            md={4}
            padding={r({ xs: '0 8px', md: '0 0 0 8px' })}
            margin={r({ xs: '16px 0 16px 0', md: '0 0 0 0' })}
          >
            {licMin.value === undefined ? (
              <Card enableHover color="blank" elevation="1" rows={1}>
                <Typography padding="8px 0" variant="body2">
                  Sin licitaciones declaradas desiertas
                </Typography>
              </Card>
            ) : (
              <Card enableHover color="blank" elevation="1" onClick={handleLicDes}>
                <Typography padding="0 0 8px" variant="body2">
                  <strong style={{ fontSize: '24px', marginRight: '10px' }}>{licDes.value}</strong>
                  Licitaciones declaradas desiertas
                </Typography>
                <Typography color="primary" fontSize="12px" variant="body2">
                  Ver detalle
                </Typography>
              </Card>
            )}
          </GridWrapper>
        </>
      ) : (
        <GridWrapper item xs={12} margin="8px 0 0">
          <DataTable
            headers={[
              { label: 'ID Licitación', value: 'bidID' },
              { label: 'Fecha de publicación', value: 'awardDate' },
              { label: 'Organismo', value: 'description' },
              { label: 'Nº de ofertas', value: 'offerNumbers' }
            ]}
            headersCellsProps={[
              { enableFilter: true, enableSort: true },
              { enableFilter: true, enableSort: true },
              { enableFilter: true, enableSort: true },
              { enableFilter: true, enableSort: true }
            ]}
            initialRows={6}
            loading={loading}
            records={tableStructure}
            recordsDecorators={[
              ({ bidID, link }) => (
                <Link href={link} target="_blank" rel="noopener noreferrer">
                  {bidID}
                </Link>
              ),
              ({ awardDate }) => formatDate(new Date(awardDate), 'dd-MM-yyyy'),
              ({ description }) => description,
              ({ offerNumbers }) => offerNumbers
            ]}
            ref={dataTableRef}
          />
        </GridWrapper>
      )}
    </GridWrapper>
  )
})

MainIndicators.defaultProps = {
  idEntity: null,
  year: new Date().getFullYear(),
  onOpen: () => undefined
}

MainIndicators.propTypes = {
  idEntity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  licDes: PropTypes.shape({
    idEntity: PropTypes.number,
    value: PropTypes.number
  }).isRequired,
  licMas: PropTypes.shape({
    idEntity: PropTypes.number,
    value: PropTypes.number
  }).isRequired,
  licMin: PropTypes.shape({
    idEntity: PropTypes.number,
    value: PropTypes.number
  }).isRequired,
  year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onOpen: PropTypes.func
}

export default MainIndicators
